import { useCallback, useState } from "react";
import { listEmployeesReq, updateEmployeeReq, archiveEmployeeReq } from "services/api";
import { EMPLOYEES_MESSAGES } from "./employees.constants";

const parseErrorMessage = (err, fallbackMessage) => {
  const data = err?.response?.data;
  if (typeof data === "string" && data) return data;
  return data?.message || data?.errors?.[0]?.msg || fallbackMessage;
};

function useEmployees({ adminId, teamId, channelId }) {
  const [employees, setEmployees] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);

  const fetchAll = useCallback(async () => {
    if (!adminId) return;
    setIsLoading(true);
    try {
      const res = await listEmployeesReq({
        admin_id: adminId,
        team_id: teamId,
        channel_id: channelId,
      });
      setEmployees(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      setErrorMessage(parseErrorMessage(err, "Failed to load employees."));
    }
    setIsLoading(false);
  }, [adminId, teamId, channelId]);

  const updateEmployee = async (id, body) => {
    try {
      setErrorMessage(null);
      setSuccessMessage(null);
      await updateEmployeeReq({ admin_id: adminId, id, body });
      setSuccessMessage(EMPLOYEES_MESSAGES.updated);
      await fetchAll();
      return true;
    } catch (err) {
      setErrorMessage(parseErrorMessage(err, "Failed to update employee."));
      return false;
    }
  };

  const archiveEmployee = async (id) => {
    try {
      setErrorMessage(null);
      setSuccessMessage(null);
      await archiveEmployeeReq({ admin_id: adminId, id });
      setSuccessMessage(EMPLOYEES_MESSAGES.archived);
      await fetchAll();
      return true;
    } catch (err) {
      setErrorMessage(parseErrorMessage(err, "Failed to archive employee."));
      return false;
    }
  };

  return {
    employees,
    isLoading,
    errorMessage,
    successMessage,
    fetchAll,
    updateEmployee,
    archiveEmployee,
    clearError: () => setErrorMessage(null),
    clearSuccess: () => setSuccessMessage(null),
  };
}

export default useEmployees;
